import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    Chip
} from '@mui/material';

import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { getJobOrders } from '../API/jobOrderAPI';
import BaseWrapper from "../../components/shared/BaseWrapper";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ButtonGroup from '@mui/material/ButtonGroup';
import { fetchAndFilterJOsBySingleDate } from '../JobOrders/utils/JobOrderFilter';
import { groupByEndorsementIdAndSort } from './utils/EndorsementGrouping';

const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
});

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));


const convertToAmPm = (time24) => {
    const [hour, minute] = time24.split(':');
    const hourNum = parseInt(hour, 10);
    const suffix = hourNum >= 12 ? 'PM' : 'AM';
    const hour12 = hourNum % 12 || 12;
    return `${hour12}:${minute} ${suffix}`;
};


const formatJobDateToDDMMMYYYY = (dateString) => {
    // Create a Date object from the "YYYY-MM-DD" string
    const dateObj = new Date(dateString);
    // Format the day with a leading zero if necessary
    const day = String(dateObj.getDate()).padStart(2, "0");
    // Define the abbreviated month names
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    // Get the month abbreviation
    const monthAbbrev = monthNames[dateObj.getMonth()];
    // Get the full year
    const year = dateObj.getFullYear();
    // Return the formatted date string
    return `${day}-${monthAbbrev}-${year}`;
};


function ManageEndorsementForm() {
    const [dateFilter, setDateFilter] = useState();
    const [groupedList, setGroupedList] = useState([]);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleSearchButtonClick = async () => {
        setIsLoading(true);

        if (dateFilter) {            
            // Single-day helper already applies 05:00 → next-day 04:59 window
            const { list } = await fetchAndFilterJOsBySingleDate({
                date: dateFilter,
                status: [],                // include all statuses
                serviceReportStatus: [],   // include all SR statuses
                billingRecordStatus: [],   // include all billing statuses
                // If need to scope results by technician, also pass userRoles & technicianId here
                getJobOrdersApi: getJobOrders,
            });

            // Keep: only jobs that already belong to an endorsement form
            const filteredJobOrders = list.filter(
                job => job.endorsement_form_id?.trim()
            );

            const sortedGroups = groupByEndorsementIdAndSort(filteredJobOrders);
            setGroupedList(sortedGroups);
        }
        setIsLoading(false);
    };


    const handleView = (group) => {
        navigate('/view-endorsement-form', { state: { group } });
    };

    const handleEdit = (group) => {
        navigate('/edit-endorsement-form', { state: { group } });
    };


    const handleSelectGroup = (group) => {
        console.log(group);
        // Navigate to the submit-endorsement-form route with state
        navigate('/submit-endorsement-form', { state: { group } });
    };
    return (
        <BaseWrapper
            titleIcon={<FactCheckIcon />}
            title="Operations"
            subTitle="Manage Endorsement Form"
            subTitleRoute="/manage-endorsement-form"
            createButtonLabel="Create Endorsement Form"
            onClickCreateButton={() => navigate("/create-endorsement-form")}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={4}>
                            <TextField
                                label="Date"
                                name="date_filter"
                                type="date"
                                value={dateFilter || ''}
                                onChange={(e) => setDateFilter(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearchButtonClick}
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <StyledHr />
            {groupedList.length > 0 && (
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ padding: "20px" }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <b>Endorsement ID</b>
                                        </TableCell>
                                        <TableCell>
                                            <b>Technicians</b>
                                        </TableCell>
                                        <TableCell colSpan={5} >
                                            <b>Job Type</b>
                                        </TableCell>
                                        <TableCell>
                                            <b>Actions</b>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupedList.map((group) => (
                                        <TableRow key={group.endorsement_form_id}>
                                            <TableCell>{group.endorsement_form_id}</TableCell>
                                            <TableCell>
                                                {group.technicians.map((tech) => (
                                                    <Typography key={tech.id}>
                                                        {tech.firstname} {tech.lastname}
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                            <TableCell colSpan={5}>
                                                {/* Display job orders as a list */}
                                                {group.jobOrders.map((jobOrder) => (
                                                    <Box
                                                        key={jobOrder.id}
                                                        sx={{
                                                            mb: 2,
                                                            p: 2,
                                                            border: '1px solid #ccc',
                                                            borderRadius: 2,
                                                            backgroundColor: '#f9f9f9',
                                                            maxWidth: 850,
                                                        }}
                                                    >
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            Client: {jobOrder.client.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Date: {formatJobDateToDDMMMYYYY(jobOrder.job_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Time: {convertToAmPm(jobOrder.start_time)} - {convertToAmPm(jobOrder.end_time)}
                                                        </Typography>
                                                        {/* <Box mt={1}>
                                                            <Typography variant="subtitle2">Job Type:</Typography>
                                                            {jobOrder.job_scope.length > 0 ? (
                                                                <ul style={{ margin: 0, padding: '0 0 0 16px', listStyleType: 'disc' }}>
                                                                    {jobOrder.job_scope.map((scope, scopeIndex) => (
                                                                        <li key={scopeIndex}>
                                                                            <Typography variant="body2">
                                                                                <b>{scope.competency}</b>
                                                                                {scope.specialization.length > 0
                                                                                    ? `: ${scope.specialization.join(', ')}`
                                                                                    : ''}
                                                                            </Typography>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <Typography variant="body2">No specific scope defined.</Typography>
                                                            )}
                                                        </Box> */}
                                                        <Box mt={1}>
                                                            <Typography variant="subtitle2">Job Type:</Typography>
                                                            {Array.isArray(jobOrder.job_types) && jobOrder.job_types.length > 0 ? (
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        flexWrap: "wrap",
                                                                        gap: 1,
                                                                        width: "100%",
                                                                        whiteSpace: "normal",
                                                                        wordBreak: "break-word",
                                                                    }}
                                                                >
                                                                    {jobOrder.job_types.map((type, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={type.replace(/_/g, " ")}
                                                                            variant="outlined"
                                                                            size="small"
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2">No job type defined.</Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </TableCell>
                                            <TableCell>
                                                <ButtonGroup
                                                    variant="outlined"
                                                    size="small"
                                                    aria-label="action button group"
                                                    sx={{
                                                        gap: 0, // Ensure no gap between buttons
                                                        padding: 0, // Remove extra space around the group if desired
                                                    }}
                                                >
                                                    <Tooltip title="View" arrow>
                                                        <Button
                                                            onClick={() => handleView(group)}
                                                            startIcon={<VisibilityIcon />}
                                                            sx={{
                                                                textTransform: "none",
                                                                minWidth: "32px", // Ensure buttons are compact
                                                                padding: "6px",
                                                                borderRadius: "4px",
                                                            }}
                                                        />
                                                    </Tooltip>

                                                    <Tooltip title="Edit" arrow>
                                                        <Button
                                                            onClick={() => handleEdit(group)}
                                                            startIcon={<EditIcon />}
                                                            sx={{
                                                                textTransform: "none",
                                                                minWidth: "32px", // Ensure buttons are compact
                                                                padding: "6px",
                                                                borderRadius: "4px",
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </ButtonGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))}


                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Grid>
            )
            }
        </BaseWrapper >

        // <ThemeProvider theme={theme}>
        //     {/* Success Snackbar */}
        //     <Snackbar
        //         open={!!successMessage}
        //         autoHideDuration={3000}
        //         anchorOrigin={{ vertical: "top", horizontal: "center" }}
        //         onClose={() => setSuccessMessage("")}
        //     >
        //         <Alert
        //             severity="success"
        //             sx={{
        //                 display: "flex",
        //                 justifyContent: "center",
        //                 alignItems: "center",
        //                 width: "auto",
        //                 maxWidth: "100%",
        //                 mx: "auto",
        //             }}
        //         >
        //             {successMessage}
        //         </Alert>
        //     </Snackbar>

        //     {/* Loading Snackbar */}
        //     <Snackbar
        //         open={isLoading}
        //         anchorOrigin={{ vertical: "top", horizontal: "center" }}
        //     >
        //         <Alert
        //             severity="info"
        //             icon={<CircularProgress size={18} />}
        //             sx={{
        //                 display: "flex",
        //                 justifyContent: "center",
        //                 alignItems: "center",
        //                 width: "auto",
        //                 maxWidth: "100%",
        //                 mx: "auto",
        //             }}
        //         >
        //             Loading, please wait...
        //         </Alert>
        //     </Snackbar>

        //     {/* Error Snackbar */}
        //     <Snackbar
        //         open={!!errorMessage}
        //         anchorOrigin={{ vertical: "top", horizontal: "center" }}
        //         onClose={() => setErrorMessage("")}
        //     >
        //         <Alert
        //             severity="error"
        //             sx={{
        //                 display: "flex",
        //                 justifyContent: "center",
        //                 alignItems: "center",
        //                 width: "auto",
        //                 maxWidth: "100%",
        //                 mx: "auto",
        //             }}
        //         >
        //             {errorMessage}
        //         </Alert>
        //     </Snackbar>

        //     <Box sx={{ padding: "40px 60px" }}>
        //         <Card
        //             variant="outlined"
        //             style={{
        //                 marginTop: "40px",
        //                 marginBottom: "40px",
        //                 borderRadius: 0,
        //             }}
        //         >
        //             <CardHeader
        //                 title={
        //                     <Box
        //                         display="flex"
        //                         alignItems="center"
        //                         justifyContent="space-between"
        //                     >
        //                         <Box display="flex" alignItems="center">
        //                             <FactCheckIcon
        //                                 sx={{
        //                                     fontSize: "40px",
        //                                     marginRight: "8px",
        //                                 }}
        //                             />
        //                             <Typography variant="h6">Operations</Typography>
        //                         </Box>
        //                         <Breadcrumbs
        //                             aria-label="breadcrumb"
        //                             separator={<NavigateNextIcon fontSize="small" />}
        //                             sx={{ color: "white" }}
        //                         >
        //                             <Typography sx={{ color: "#ffffff99" }}>
        //                                 Operations
        //                             </Typography>
        //                             <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
        //                                 Manage Endorsement Form
        //                             </Typography>
        //                         </Breadcrumbs>
        //                     </Box>
        //                 }
        //                 sx={{
        //                     backgroundColor: "#1e5d45",
        //                     color: "white",
        //                     padding: "16px",
        //                 }}
        //             />
        //             <CardContent sx={{ padding: "20px" }}>
        //                 <Card variant="outlined" sx={{ padding: "20px" }}>
        //                     <CardHeader
        //                         title={
        //                             <Grid container alignItems="center" spacing={2}>
        //                                 <Grid item xs={12} sm={6}>
        //                                     <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        //                                         Manage Endorsement Form
        //                                     </Typography>
        //                                 </Grid>
        //                                 <Grid
        //                                     item
        //                                     xs={12}
        //                                     sm={6}
        //                                     container
        //                                     justifyContent={{ xs: "center", sm: "flex-end" }}
        //                                 >
        //                                     <Button
        //                                         component={Link}
        //                                         onClick={() => navigate("/create-endorsement-form")}
        //                                         variant="contained"
        //                                         color="primary"
        //                                         startIcon={<AddIcon />}
        //                                     >
        //                                         Create Endorsement Form
        //                                     </Button>
        //                                 </Grid>
        //                             </Grid>
        //                         }

        //                         sx={{ marginBottom: 2 }}
        //                     />

        //                     <Grid container spacing={3} sx={{ mb: 2 }}>
        //                         <Grid item xs={12}>
        //                             <Grid container spacing={2} alignItems="flex-end">
        //                                 <Grid item xs={2}>
        //                                     <TextField
        //                                         label="Date"
        //                                         name="date_filter"
        //                                         type="date"
        //                                         value={dateFilter || ''}
        //                                         onChange={(e) => setDateFilter(e.target.value)}
        //                                         InputLabelProps={{ shrink: true }}
        //                                         fullWidth
        //                                     />
        //                                 </Grid>
        //                                 <Grid item xs={3}>
        //                                     <Button
        //                                         variant="contained"
        //                                         color="primary"
        //                                         onClick={handleSearchButtonClick}
        //                                     >
        //                                         Search
        //                                     </Button>
        //                                 </Grid>
        //                             </Grid>
        //                         </Grid>
        //                     </Grid>
        //                     <StyledHr />
        //                     <Grid item xs={12}>
        //                         <Card variant="outlined" sx={{ padding: "20px" }}>
        //                             <TableContainer>
        //                                 <Table>
        //                                     <TableHead>
        //                                         <TableRow>
        //                                             <TableCell>
        //                                                 <b>Endorsement ID</b>
        //                                             </TableCell>
        //                                             <TableCell>
        //                                                 <b>Accountable Technician</b>
        //                                             </TableCell>
        //                                             <TableCell>
        //                                                 <b>Technicians</b>
        //                                             </TableCell>
        //                                             <TableCell colSpan={5} >
        //                                                 <b>Job Scope</b>
        //                                             </TableCell>
        //                                             <TableCell>
        //                                                 <b>Actions</b>
        //                                             </TableCell>
        //                                         </TableRow>
        //                                     </TableHead>
        //                                     <TableBody>
        //                                         {groupedList.map((group) => (
        //                                             <TableRow key={group.endorsement_form_id}>
        //                                                 <TableCell>{group.endorsement_form_id}</TableCell>
        //                                                 <TableCell>{group.endorsement_form_acct_tech}</TableCell>
        //                                                 <TableCell>
        //                                                     {group.technicians.map((tech) => (
        //                                                         <Typography key={tech.id}>
        //                                                             {tech.firstname} {tech.lastname}
        //                                                         </Typography>
        //                                                     ))}
        //                                                 </TableCell>
        //                                                 <TableCell colSpan={5}>
        //                                                     {/* Display job orders as a list */}
        //                                                     {group.jobOrders.map((jobOrder) => (
        //                                                         <Box
        //                                                             key={jobOrder.id}
        //                                                             sx={{
        //                                                                 mb: 2,
        //                                                                 p: 2,
        //                                                                 border: '1px solid #ccc',
        //                                                                 borderRadius: 2,
        //                                                                 backgroundColor: '#f9f9f9',
        //                                                             }}
        //                                                         >
        //                                                             <Typography variant="subtitle1" fontWeight="bold">
        //                                                                 Client: {jobOrder.client.name}
        //                                                             </Typography>
        //                                                             <Typography variant="body2" color="textSecondary">
        //                                                                 Time: {jobOrder.start_time} - {jobOrder.end_time}
        //                                                             </Typography>
        //                                                             <Box mt={1}>
        //                                                                 <Typography variant="subtitle2">Job Scope:</Typography>
        //                                                                 {jobOrder.job_scope.length > 0 ? (
        //                                                                     <ul style={{ margin: 0, padding: '0 0 0 16px', listStyleType: 'disc' }}>
        //                                                                         {jobOrder.job_scope.map((scope, scopeIndex) => (
        //                                                                             <li key={scopeIndex}>
        //                                                                                 <Typography variant="body2">
        //                                                                                     <b>{scope.competency}</b>
        //                                                                                     {scope.specialization.length > 0
        //                                                                                         ? `: ${scope.specialization.join(', ')}`
        //                                                                                         : ''}
        //                                                                                 </Typography>
        //                                                                             </li>
        //                                                                         ))}
        //                                                                     </ul>
        //                                                                 ) : (
        //                                                                     <Typography variant="body2">No specific scope defined.</Typography>
        //                                                                 )}
        //                                                             </Box>
        //                                                         </Box>
        //                                                     ))}
        //                                                 </TableCell>
        //                                                 <TableCell>
        //                                                     <Box display="flex" alignItems="center">
        //                                                         <Button
        //                                                             variant="contained"
        //                                                             color="primary"
        //                                                             onClick={() => handleView(group)}
        //                                                             sx={{
        //                                                                 marginBottom: { xs: "10px", lg: "0" },
        //                                                                 marginRight: { lg: "10px" },
        //                                                                 width: { xs: "100%", lg: "auto" },
        //                                                             }}
        //                                                         >
        //                                                             View
        //                                                         </Button>
        //                                                         <Button
        //                                                             variant="contained"
        //                                                             color="primary"
        //                                                             onClick={() => handleEdit(group)}
        //                                                             sx={{
        //                                                                 marginBottom: { xs: "10px", lg: "0" },
        //                                                                 marginRight: { lg: "10px" },
        //                                                                 width: { xs: "100%", lg: "auto" },
        //                                                             }}
        //                                                         >
        //                                                             Edit
        //                                                         </Button>
        //                                                     </Box>
        //                                                 </TableCell>
        //                                             </TableRow>
        //                                         ))}


        //                                     </TableBody>
        //                                 </Table>
        //                             </TableContainer>
        //                         </Card>
        //                     </Grid>

        //                 </Card>
        //             </CardContent>
        //         </Card>
        //     </Box>
        // </ThemeProvider>
    );
}

export default ManageEndorsementForm;